import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button3D } from '@/components/ui/button-3d';
import { Building2, Check, Loader2 } from 'lucide-react';
import type { Company } from '@/types/database';
import { useFeature } from '@/hooks/useFeature';
import { StorageImage } from '@/components/ui/StorageImage';

interface SelectActiveCompanyModalProps {
  open: boolean;
  companies: Company[];
  activeCompanyId: string | null;
  onSelectCompany: (companyId: string) => void | Promise<void>;
  saving?: boolean;
}

export function SelectActiveCompanyModal({
  open,
  companies,
  activeCompanyId,
  onSelectCompany,
  saving = false,
}: SelectActiveCompanyModalProps) {
  const [selectedId, setSelectedId] = useState<string>(activeCompanyId || companies[0]?.id || '');
  const multipleCompanies = useFeature('multiple_companies');

  const handleConfirm = async () => {
    const targetId = selectedId || companies[0]?.id;
    if (targetId) {
      await onSelectCompany(targetId);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={() => {}}>
        <DialogContent 
          className="sm:max-w-md [&>button]:hidden"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader className="text-center sm:text-left space-y-2">
            <DialogTitle className="text-xl font-bold">
              Escolha sua empresa ativa
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Você possui {companies.length} empresas cadastradas. No plano Gratuito, você pode manter 1 empresa ativa para emitir orçamentos.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-3 max-h-60 overflow-y-auto pr-1">
            {companies.map((company) => {
              const isSelected = selectedId === company.id;
              return (
                <div
                  key={company.id}
                  onClick={() => setSelectedId(company.id)}
                  className={`flex items-center justify-between p-3.5 rounded-xl border-2 cursor-pointer transition-all ${
                    isSelected
                      ? 'border-primary bg-primary/5 shadow-sm'
                      : 'border-border hover:border-muted-foreground/30 bg-card'
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {company.logo_url ? (
                      <StorageImage
                        src={company.logo_url}
                        alt={company.name}
                        className="h-9 w-9 shrink-0 rounded-lg object-cover"
                        fallback={
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
                            <Building2 className="h-5 w-5 text-muted-foreground" />
                          </div>
                        }
                      />
                    ) : (
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
                        <Building2 className="h-5 w-5 text-muted-foreground" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="font-semibold text-sm truncate text-foreground">
                        {company.name}
                      </p>
                      {company.cnpj_cpf && (
                        <p className="text-xs text-muted-foreground truncate">
                          {company.cnpj_cpf}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0 ml-2">
                    {isSelected ? (
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground">
                        <Check className="h-4 w-4" />
                      </div>
                    ) : (
                      <div className="h-6 w-6 rounded-full border-2 border-muted-foreground/30" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex flex-col gap-2 pt-2 border-t">
            <Button3D
              onClick={handleConfirm}
              disabled={saving}
              className="w-full text-base"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                'Confirmar empresa ativa'
              )}
            </Button3D>

            <Button3D
              color="amber"
              onClick={() => multipleCompanies.require()}
              disabled={saving}
              className="w-full"
            >
              Desbloquear todas com o PRO
            </Button3D>
          </div>
        </DialogContent>
      </Dialog>

    </>
  );
}

export default SelectActiveCompanyModal;
